"use client";

import { AnimatePresence, motion } from "motion/react";
import { cn } from "@/lib/utils";

interface UnreadBadgeProps {
  /** Unread count. 0 (or less) renders nothing. >99 caps to "99+". */
  count: number;
  /** `sm` — tab-icon scale (default); `md` — launcher-FAB scale. */
  size?: "sm" | "md";
  /** Absolute positioning supplied by the caller (e.g.
   *  `"absolute -top-1.5 -right-2"`). The badge anchors to the nearest
   *  positioned ancestor. */
  className?: string;
  /** Render as a countless dot (same halo, no number) — the news
   *  unread indicator. Shows whenever `dot` is true, independent of `count`.
   *  When both a positive `count` and `dot` apply, the count wins. */
  dot?: boolean;
  /** Accessible label. Required for `dot` mode (no number to announce);
   *  falls back to the count for the counted badge. */
  label?: string;
}

const SIZE_CLASSES: Record<NonNullable<UnreadBadgeProps["size"]>, string> = {
  sm: "h-3.5 min-w-3.5 px-0.75 text-[8px]",
  md: "h-5 min-w-5 px-1.5 text-[10px]",
};

// Motion matches the console design system's base (200ms) and fast (120ms) durations and its standard easing; the
// values are copied here on purpose (the widget never imports the private package, and its sourcemaps are scanned).
const FADE_IN = { duration: 0.2, ease: [0.2, 0, 0, 1] } as const;
const FADE_OUT = { duration: 0.12, ease: [0.2, 0, 0, 1] } as const;

/**
 * Shared unread-count badge - a red pill that only FADES: in on appear, out when the count drops
 * to 0. No scale, spring or pulsing ring, so it never "flies" over the FAB or a tab icon. A count
 * change swaps the number in place (the same element, no remount, no animation).
 *
 * Used on the bottom-nav messages tab and the launcher FAB so both share one behaviour. The
 * caller positions it via `className` and the parent must NOT be `overflow-hidden` (it would
 * shear the soft glow - see `bottom-nav.tsx`).
 */
export function UnreadBadge({ count, size = "sm", className, dot, label }: UnreadBadgeProps) {
  // A positive count always renders as a number; otherwise `dot` renders a
  // countless indicator.
  const asDot = dot && count <= 0;
  const visible = count > 0 || asDot;
  return (
    <AnimatePresence>
      {visible && (
        <motion.span
          // Constant key: count / dot changes update the content, never remount.
          key="badge"
          role="status"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: FADE_IN }}
          exit={{ opacity: 0, transition: FADE_OUT }}
          className={cn("inline-flex", className)}
          aria-label={label ?? `${count} unread`}
        >
          {/* Constant soft halo - blurred disc so the badge always reads as gently "lit". */}
          <span
            aria-hidden="true"
            className="absolute inset-0 -z-10 rounded-full bg-wx-danger opacity-40 blur-[2px]"
          />
          {/* The crisp badge on top. */}
          <span
            className={cn(
              "relative flex items-center justify-center",
              "rounded-full bg-wx-danger font-bold leading-none text-white",
              // Same size as the counted badge — dot mode just drops the glyph,
              // so the empty circle matches the numbered pill's diameter.
              SIZE_CLASSES[size],
            )}
          >
            {asDot ? null : count > 99 ? "99+" : count}
          </span>
        </motion.span>
      )}
    </AnimatePresence>
  );
}
